/**
 * MoneyGram GFFP Bank Fields Service Implementation
 * Handles fetching bank account field requirements for BANK_DEPOSIT and DIRECT_TO_ACCT transactions
 */

import type { AxiosInstance } from "axios";
import { getActiveUserLanguageTag } from "@/utils/user-language";
import type {
	GetBankFieldsRequest,
	GffpBankFieldsResponse,
	GffpBankFieldsService,
} from "../contracts/gffp-bank-fields.contract";
import type { RequestConfig } from "../types/common";

export class MoneyGramGffpBankFieldsService implements GffpBankFieldsService {
	constructor(private readonly httpClient: AxiosInstance) {}

	async getBankFields(
		request: GetBankFieldsRequest,
		config?: RequestConfig,
	): Promise<GffpBankFieldsResponse> {
		try {
			// Build query parameters
			const params = new URLSearchParams({
				destinationCountryCode: request.destinationCountryCode,
				sendAmount: request.sendAmount.toString(),
				sendCurrencyCode: request.sendCurrencyCode,
				receiveCurrencyCode: request.receiveCurrencyCode,
				serviceOptionCode: request.serviceOptionCode,
				serviceOptionRoutingCode: request.serviceOptionRoutingCode,
				userLanguage: request.userLanguage || getActiveUserLanguageTag(),
			});

			// Add optional subdivision code if provided
			if (request.destinationCountrySubdivisionCode) {
				params.append(
					"destinationCountrySubdivisionCode",
					request.destinationCountrySubdivisionCode,
				);
			}

			const response = await this.httpClient.get(
				`/config-data/bank/fields?${params.toString()}`,
				{
					signal: config?.signal,
					timeout: config?.timeout,
				},
			);

			return this.transformBankFieldsResponse(response.data);
		} catch (error) {
			throw this.handleApiError(error, "Failed to fetch bank fields");
		}
	}

	/**
	 * Transform API response to our domain model
	 */
	private transformBankFieldsResponse(data: unknown): GffpBankFieldsResponse {
		// Validate response structure
		if (!data || typeof data !== "object") {
			throw new Error("Invalid bank fields response format");
		}

		const responseData = data as Record<string, unknown>;

		if (!Array.isArray(responseData.fieldInfo)) {
			throw new Error("Missing or invalid fieldInfo in bank fields response");
		}

		return {
			fieldInfo: responseData.fieldInfo,
		};
	}

	/**
	 * Handle API errors with context
	 */
	private handleApiError(error: unknown, context: string): Error {
		if (error instanceof Error) {
			return new Error(`${context}: ${error.message}`);
		}
		return new Error(context);
	}
}
